// AccessPage.jsx — CONTAINER (dono da lógica). Monta { vm, actions } reais e renderiza a AccessView
// (View pura do Claude Design). Formato View/ViewModel à risca (docs/DESIGN-FLOW.md).
// O visual é da View; aqui há estado e chamadas à API. A tradução para o vm mora em
// access/accessVmBuilder.js (função pura, coberta por access/__tests__/access.contract.test.js).
import { useState, useEffect, useCallback } from 'react';

import accessService from '../services/accessService';
import usersService from '../services/usersService';
import invitesService from '../services/invitesService';
import professionalsService from '../services/professionalsService';
import photoService from '../services/photoService';
import { authService } from '../services/authService';
import useSnackbar from '../hooks/useSnackbar';

import AppSnackbar from '../components/ui/AppSnackbar';
import AccessView from './access/AccessView';
import {
  montarVm, FORM_FECHADO, mapaDePermissoes, permissoesParaApi, aplicarRegras, validarEmail, chaveDaLinha,
} from './access/accessVmBuilder';

const fotoDe = (donoId, profissionalId) => photoService.getProfilePhotoUrl(donoId, profissionalId);

export default function AccessPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [profissionais, setProfissionais] = useState([]);
  const [form, setForm] = useState(FORM_FECHADO);
  const [editando, setEditando] = useState(null); // { key, permissoes, salvando }
  const [removendo, setRemovendo] = useState(null); // { key, emAndamento }

  // A primeira carga mostra o esqueleto; depois de compartilhar, editar ou remover, a lista se
  // atualiza por baixo, sem a tela piscar para o carregamento.
  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) { setCarregando(true); setErro(false); }
    try {
      const resultado = await accessService.getOverview();
      setItens(resultado?.items || []);
      setErro(false);
    } catch (err) {
      console.error('Erro ao carregar acessos:', err);
      if (silencioso) showError('Não foi possível atualizar a lista de acessos');
      else setErro(true);
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [showError]);

  useEffect(() => { carregar(); }, [carregar]);

  // Só os profissionais do próprio dono (os compartilhados com ele não podem ser repassados).
  const carregarProfissionais = useCallback(async () => {
    try {
      const data = await professionalsService.getAll();
      setProfissionais((data?.professionals || []).filter((p) => !p.isShared));
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
    }
  }, []);

  const porChave = (key) => itens.find((item) => chaveDaLinha(item) === key);
  const mudarForm = (parcial) => setForm((f) => ({ ...f, ...parcial }));
  const fecharForm = () => setForm(FORM_FECHADO);

  const buscarEmail = async () => {
    const email = form.email.trim();
    if (!email) return;
    const erroEmail = validarEmail(email);
    if (erroEmail) { mudarForm({ erroEmail }); return; }
    mudarForm({ erroEmail: null, buscando: true, resultado: null });
    // A resposta só vale se o e-mail no campo ainda for o buscado (a pessoa pode ter mudado ou fechado o form).
    const seAindaFor = (parcial) => setForm((f) => (f.email.trim() === email ? { ...f, ...parcial } : f));
    try {
      const user = await usersService.lookupByEmail(email);
      seAindaFor({ resultado: { found: true, user } });
    } catch (err) {
      if (err.response?.status === 404) seAindaFor({ resultado: { found: false } });
      else showError('Erro ao buscar usuário');
    } finally {
      mudarForm({ buscando: false });
    }
  };

  const compartilhar = async () => {
    const user = form.resultado?.found ? form.resultado.user : null;
    if (!user || !form.profissionalId) return;
    mudarForm({ compartilhando: true });
    try {
      await accessService.grantAccess({
        professionalId: form.profissionalId,
        userId: user.id,
        permissions: permissoesParaApi(form.permissoes),
      });
      showSuccess(`Acesso compartilhado com ${user.name}`);
      fecharForm();
      carregar({ silencioso: true });
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao compartilhar acesso');
      mudarForm({ compartilhando: false });
    }
  };

  const convidar = async () => {
    const email = form.email.trim();
    if (!email) return;
    try {
      await invitesService.create({ email, context: 'professional_access', message: 'Convite para colaborar no Agendário' });
      showSuccess(`Convite enviado para ${email}`);
      mudarForm({ resultado: null, email: '' });
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao enviar convite');
    }
  };

  const salvarEdicao = async (key) => {
    const item = porChave(key);
    if (!item || editando?.key !== key) return;
    setEditando((e) => ({ ...e, salvando: true }));
    try {
      await accessService.updateAccess(item.professionalId, item.grantedToUserId, { permissions: permissoesParaApi(editando.permissoes) });
      showSuccess('Permissões atualizadas');
      setEditando(null);
      carregar({ silencioso: true });
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao atualizar permissões');
      setEditando((e) => (e ? { ...e, salvando: false } : e));
    }
  };

  // O alvo do DELETE é sempre quem RECEBEU o acesso. A API aceita as duas partes (AccessController »
  // RevokeAccessByEitherPartyAsync): o dono remove, e quem recebeu pode sair.
  const confirmarRemocao = async (key) => {
    const item = porChave(key);
    if (!item) return;
    setRemovendo({ key, emAndamento: true });
    try {
      await accessService.revokeAccess(item.professionalId, item.grantedToUserId);
      showSuccess(item.direction === 'given' ? 'Acesso removido' : 'Você saiu do compartilhamento');
      // A linha sai junto com a confirmação: sem isto ela voltaria ao normal (com o botão de remover) até a lista
      // nova chegar, e a View não saberia se a pessoa cancelou ou se deu certo.
      setItens((lista) => lista.filter((i) => chaveDaLinha(i) !== key));
      setRemovendo(null);
      carregar({ silencioso: true });
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao remover acesso');
      setRemovendo({ key, emAndamento: false });
    }
  };

  const actions = {
    onToggleGrantForm: () => {
      if (form.aberto) { fecharForm(); return; }
      if (!profissionais.length) carregarProfissionais();
      mudarForm({ aberto: true });
    },
    onChangeGrantProfessional: (id) => mudarForm({ profissionalId: id }),
    onChangeEmail: (email) => mudarForm({ email, erroEmail: null, resultado: null }),
    onSearchEmail: buscarEmail,
    onChangeGrantPermission: (chave, nivel) => setForm((f) => ({ ...f, permissoes: aplicarRegras(f.permissoes, chave, nivel) })),
    onGrant: compartilhar,
    onSendInvite: convidar,
    onCancelGrant: fecharForm,
    onStartEdit: (key) => {
      const item = porChave(key);
      if (!item) return;
      const permissoes = mapaDePermissoes(item.permissions);
      permissoes.professional = Math.max(permissoes.professional, 1);
      setRemovendo(null);
      setEditando({ key, permissoes, salvando: false });
    },
    onChangeEditPermission: (key, chave, nivel) => setEditando((e) => (e?.key === key ? { ...e, permissoes: aplicarRegras(e.permissoes, chave, nivel) } : e)),
    onSaveEdit: salvarEdicao,
    onCancelEdit: () => setEditando(null),
    onAskRevoke: (key) => { setEditando(null); setRemovendo({ key, emAndamento: false }); },
    onConfirmRevoke: confirmarRemocao,
    onCancelRevoke: () => setRemovendo(null),
    onRetry: () => carregar(),
  };

  const vm = montarVm({
    itens, carregando, erro, form, profissionais,
    usuarioId: authService.getUserInfo()?.userId, editando, removendo, fotoDe,
  });

  return (
    <>
      <AccessView vm={vm} actions={actions} />
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
